import React, { useState, useEffect, useMemo } from 'react';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { Formik, Form } from 'formik';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import PageLoader from 'components/PageLoader';
import SubmitDetails from './components/SubmitDetails';
import SignatureModal from './components/SignatureModal';
import styles from './styles';
import OutsideClockInModal from './components/OutsideClockInModal';
import { useCheckOutsideClockIn } from '../../hooks/useCheckOutsideClockIn';
import { emitEvent } from 'helpers/emitEvent.sockets';
import { SHEET_EVENTS } from 'subscriptions/sheetEvents.sockets';
import { EVENT_NAMESPACES } from 'helpers/eventNamespaces.sockets';
import $api from 'http/index';
import { getOptions } from 'helpers/getOptions';
import currentWorklogActions from 'store/currentWorklog/actions';
import { connect, useDispatch, useSelector } from 'react-redux';
import { addSubmittedSheet } from 'store/schedule/schedulePageOperation';
import { worklogsActions } from 'store/worklogs';
import { v4 as uuidv4 } from 'uuid';
import { useShouldDisplayGenerateModal } from '../hooks/useShouldDisplayGenerateModal';
import GenerateMaterialCertsModal from './components/GenerateMaterialCertsModal';

export function validateEmail(email) {
  var re = /\S+@\S+\.\S+/;
  return re.test(email);
}

const validation = (values) => {
  const correctEmail = 'Enter valid email address';
  const requiredFieldError = "This field can't be blank";
  const numberError = 'Phone number must be 10 digits';

  const errors = {};
  if (values.contractorSignature && !values.contractorName.trim()) {
    errors.contractorName = "This field can't be blank if there is a signature.";
  }
  if (values.contractorEmail?.trim() && !validateEmail(values.contractorEmail)) {
    errors.contractorEmail = correctEmail;
  }
  if (
    values.contractorPhoneNumber?.trim() &&
    !(values.contractorPhoneNumber.trim()?.length === 10)
  ) {
    errors.contractorPhoneNumber = numberError;
  }

  const dotContacts = {};
  values.dot.forEach((el, i) => {
    const tmpErrObj = {};
    if (el.Signature && !el.name?.trim()) {
      tmpErrObj.name = "This field can't be blank if there is a signature.";
    }

    if (el.email.trim() && !validateEmail(el.email)) {
      tmpErrObj.email = correctEmail;
    }

    if (el.phoneNumber.trim() && !(el.phoneNumber.trim()?.length === 10)) {
      tmpErrObj.phoneNumber = numberError;
    }
    if (Object.keys(tmpErrObj).length) {
      dotContacts[i] = tmpErrObj;
    }
    if (
      dotContacts[0] &&
      !values.dot?.[0]?.name?.trim() &&
      !values.dot?.[0]?.email?.trim() &&
      !values.dot?.[0]?.phoneNumber?.trim() &&
      !values.dot?.[0]?.Signature
    ) {
      delete dotContacts[0];
    }
  });

  if (Object.keys(dotContacts).length) {
    errors.dot = dotContacts;
  }

  const contacts = {};
  values.contractorAlternateContacts.forEach((el, i) => {
    const tmpErrObj = {};
    if (!el.name.trim()) {
      tmpErrObj.name = requiredFieldError;
    }

    if (el.email.trim() && !validateEmail(el.email)) {
      tmpErrObj.email = correctEmail;
    }

    if (el.phoneNumber.trim() && !(el.phoneNumber.trim()?.length === 10)) {
      tmpErrObj.phoneNumber = numberError;
    }
    if (Object.keys(tmpErrObj).length) {
      contacts[i] = tmpErrObj;
    }
  });

  if (Object.keys(contacts).length) {
    errors.contractorAlternateContacts = contacts;
  }

  return errors;
};

const SubmitModal = ({
  classes,
  closeModal,
  closeSubmitChangesModal,
  modalType,
  sheet,
  openSnackbar
}) => {
  const dispatch = useDispatch();
  const projectStore = useSelector((store) => store.projectTypes.projects)
  const submittedChangesModal = useSelector((store) => store.currentWorklog.submittedChangesModal);

  const [signatureModal, setSignatureModal] = useState({
    isOpen: false,
    type: 'dot',
  });

  const [outsideClockInModal, setOutsideClockInModal] = useState(false);
  const [generateMaterialCertsModal, setGenerateMaterialCertsModal] = useState(false);
  const [contractorsAlternateContacts, setContractorsAlternateContacts] = useState([]); //fully alternate contacts obj
  const [contactDropdownSelector, setContactDropdownSelector] = useState(false);

  useEffect(() => {
    setContractorsAlternateContacts(sheet?.alternateContacts || []);
  }, [sheet]);

  const openSignatureModal = (type, i) => () => {
    setSignatureModal({ type, isOpen: true });
  };

  const closeSignatureModal = () => {
    setSignatureModal((prev) => ({ ...prev, isOpen: false }));
  };

  const closeOutsideClockInModal = (setSubmitting) => () => {
    setOutsideClockInModal(null);
    setSubmitting(false);
  };

  const closeGenerateMaterialCertsModal = (setSubmitting) => () => {
    setGenerateMaterialCertsModal(false);
    setSubmitting(false);
  }

  const contactsAdapter = (contactsArr) => {
    return contactsArr.map((el) => ({
      name: el.name,
      email: el.email,
      phoneNumber: el.phoneNumber,
      image: el.Signature,
    }));
  };

  const shouldDisplayGenerateMaterialsModal = useShouldDisplayGenerateModal(sheet);


  const submitSheet = async (data, setSubmitting) => {
    try {
      setSubmitting(true);

      const res = await $api.post(
        `${process.env.REACT_APP_BASE_URL}/sheets/${sheet._id}/submit`,
        data,
        getOptions()
      );
      dispatch(worklogsActions.setForceUpdate(true));
      dispatch(currentWorklogActions.setWorklogSheet(res.data));
      dispatch(addSubmittedSheet(res.data));
      emitEvent(EVENT_NAMESPACES.sheets, SHEET_EVENTS.submit, sheet._id);
      openSnackbar('success', 'Successfully Submitted!');
      closeModal();

      if (submittedChangesModal?.onFinish) {
        submittedChangesModal.onFinish();
      }
      dispatch(currentWorklogActions.closeSubmittedChangesModal());
    } catch (e) {
      openSnackbar('error', e?.response?.data?.message?.join('\n') || e.mesage);
      // openSnackbar('error', 'On Job Time is not within Clock-in Time range');
    } finally {
      setSubmitting(false);
    }
  };

  const combineDataFromValues = (values) => {
    const data = {
      notes: values.notes?.trim(),
      dwlVerificationContacts: values.dwlVerificationContacts || [],
    };
    const dot = contactsAdapter([...values.dot]);
    const contractor = [
      {
        name: values.contractorName,
        email: values.contractorEmail,
        image: values.contractorSignature,
        phoneNumber: values.contractorPhoneNumber,
      },
      ...contactsAdapter([...values.contractorAlternateContacts]),
    ];
    data.dot = dot;
    data.contractor = contractor;

    if (sheet.submittedAt) {
      data.skipCertsGeneration = true;
    }

    return data;
  }

  const submitOutsideClockInModal = (setSubmitting, values) => async () => {
    const data = combineDataFromValues(values)
    setOutsideClockInModal(null);

    if (shouldDisplayGenerateMaterialsModal) {
      return setGenerateMaterialCertsModal(true);
    }

    submitSheet(data, setSubmitting);
  };

  const submitGenerateMaterialCerts = (setSubmitting, values) => async (skipCertsGeneration) => {
    const data = combineDataFromValues(values)
    setGenerateMaterialCertsModal(false);

    data.skipCertsGeneration = !!skipCertsGeneration;

    submitSheet(data, setSubmitting);
  }


  const setSignature = (type, setFieldValue) => (data) => {
    setFieldValue(`${type}Signature`, data);
    closeSignatureModal();
  };

  const removeSignature = (fieldName, setFieldValue) => () => {
    setFieldValue(`${fieldName}`, null);
  };

  const handleAppendDOTContact = (setFieldValue, values) => {
    setFieldValue('dot', [
      ...values.dot,
      {
        name: '',
        email: '',
        phoneNumber: '',
        new: true,
        _id: uuidv4(),
      },
    ]);
  };

  const handleAppendContact = (setFieldValue, values) => (e) => {
    const { value } = e?.target;
    if (value === 'newEmpty') {
      setFieldValue('contractorAlternateContacts', [
        ...values.contractorAlternateContacts,
        {
          name: '',
          title: '',
          email: '',
          phoneNumber: '',
          new: true,
          _id: uuidv4(),
        },
      ]);
      setContactDropdownSelector(false);
      return;
    }

    setFieldValue('contractorAlternateContacts', [
      ...values.contractorAlternateContacts,
      contractorsAlternateContacts.find((el) => el._id === value),
    ]);
    setContactDropdownSelector(false);
  };

  const removeAlternateContacts = (values, setFieldValue, i, fieldName) => () => {
    // fieldName = contractorAlternateContacts
    setFieldValue(
      fieldName,
      values[fieldName].filter((_, index) => index != i)
    );
  };

  const originalProject = useMemo(() => {
    if (sheet.project.submissionClone && !projectStore?.isLoading) {
      return projectStore?.data?.find(p => p._id === sheet.project.oldProjectId)
    }
    return sheet.project;
  }, [sheet, projectStore])

  const initialValues = {
    notes: sheet.notes || '',
    dotName: '',
    dotEmail: '',
    dotSignature: null,
    dotPhoneNumber: '',
    dot: [
      {
        name: '',
        email: '',
        dotSignature: null,
        phoneNumber: '',
      },
    ],
    contractorName: sheet?.primaryContact?.name || '',
    contractorEmail: sheet?.primaryContact?.email || '',
    contractorPhoneNumber: sheet?.primaryContact?.phoneNumber || '',
    contractorSignature: null,
    contractorAlternateContacts: [],
    dwlVerificationContacts: originalProject?.dwlVerificationContacts || [],
  };

  const { checkOutsideClockIn } = useCheckOutsideClockIn({
    hours: sheet.hours,
    sheetId: sheet._id,
    workers: sheet.workers,
  });

  const onClearAll = (values, resetForm) => () => {
    resetForm({
      values: {
        ...initialValues,
        notes: '',
        contractorName: '',
        contractorEmail: '',
        contractorPhoneNumber: '',
        dwlVerificationContacts: values.dwlVerificationContacts || initialValues.dwlVerificationContacts
      }
    })
  }

  return (
    <Dialog
      open
      onClose={closeModal}
      PaperProps={{
        classes: {
          root: classes.dialogRoot,
        },
      }}
      disableEscapeKeyDown
    >
      <div className={classes.formWrapper} role="presentation">
        <Formik
          validate={validation}
          validateOnChange={false}
          validateOnBlur={true}
          initialValues={initialValues}
          enableReinitialize={false}
          onSubmit={async (values, { setSubmitting }) => {
            const data = combineDataFromValues(values);
            const res = await checkOutsideClockIn();

            if (res.length) setOutsideClockInModal(res);
            else if (shouldDisplayGenerateMaterialsModal) {
              setGenerateMaterialCertsModal(true);
            }
            else {
              submitSheet(data, setSubmitting)
            }
          }}
        >
          {({
            errors,
            touched,
            values,
            handleChange,
            handleBlur,
            isSubmitting,
            setFieldValue,
            setSubmitting,
            resetForm,
          }) => (
            <PageLoader loading={isSubmitting}>
              <Form autoComplete="off" className={classes.formFlex}>
                <div>
                  <div className={classes.headerWrapper}>
                    <span className={classes.headerTextWrapper}>
                      <Typography variant="h3" style={{ marginBottom: '10px' }}>
                        {modalType} sheet
                      </Typography>
                    </span>

                    <span className={classes.headerButtonWrapper}>
                      <Button
                        color="primary"
                        variant="text"
                        className={classes.clearAllButton}
                        onClick={onClearAll(values, resetForm)}
                        disableTouchRipple
                      >
                        Clear All
                      </Button>
                      <IconButton
                        className={classes.closeButton}
                        onClick={closeModal}
                        aria-label="Close"
                        disableTouchRipple
                      >
                        <CloseIcon />
                      </IconButton>
                    </span>
                  </div>

                  <SubmitDetails
                    sheet={sheet}
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    openSignatureModal={openSignatureModal}
                    removeSignature={removeSignature}
                    contractorsAlternateContacts={contractorsAlternateContacts}
                    handleAppendContact={handleAppendContact}
                    removeAlternateContacts={removeAlternateContacts}
                    setContactDropdownSelector={setContactDropdownSelector}
                    contactDropdownSelector={contactDropdownSelector}
                    handleAppendDOTContact={handleAppendDOTContact}
                  />
                </div>

                <div className={classes.buttonHolder}>
                  <Button
                    color="secondary"
                    variant="outlined"
                    className={classes.cancelButton}
                    onClick={closeModal}
                    disabled={isSubmitting}
                    disableTouchRipple
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="outlined"
                    className={classes.saveButton}
                    disabled={isSubmitting}
                    disableTouchRipple
                  >
                    Submit
                  </Button>
                </div>

                {signatureModal.isOpen && (
                  <SignatureModal
                    closeModal={closeSignatureModal}
                    submitModal={setSignature(signatureModal.type, setFieldValue)}
                    type={signatureModal.type}
                  />
                )}
                {outsideClockInModal && (
                  <OutsideClockInModal
                    users={outsideClockInModal}
                    closeModal={closeOutsideClockInModal(setSubmitting)}
                    submitModal={submitOutsideClockInModal(setSubmitting, values)}
                  />
                )}
                {generateMaterialCertsModal ? (
                  <GenerateMaterialCertsModal
                    closeModal={closeGenerateMaterialCertsModal(setSubmitting)}
                    submitModal={submitGenerateMaterialCerts(setSubmitting, values)}
                  />
                ) : null}
              </Form>
            </PageLoader>
          )}
        </Formik>
      </div>
    </Dialog>
  );
};

SubmitModal.propTypes = {
  classes: propTypes.object.isRequired,
  sheet: propTypes.object.isRequired,
  openSnackbar: propTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => {
  return {
    closeModal: () => {
      dispatch(currentWorklogActions.closeSubmitModal());
    },
  };
};

const mapStateToProps = ({ currentWorklog }) => ({
  isOpen: currentWorklog.submitModal.isOpen,
  modalType: currentWorklog.submitModal.modalType,
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(SubmitModal));
